import React from "react";
import { View, TouchableOpacity, type ViewStyle } from "react-native";
import { useI18n } from "@/context/I18nContext";
import type { TranslationKeys } from "@chinese-vendor/i18n";

export type CardVariant = "default" | "elevated" | "outlined";

export interface CardProps {
  children: React.ReactNode;
  variant?: CardVariant;
  /** When provided the card renders as a pressable TouchableOpacity. */
  onPress?: () => void;
  /** Required for screen readers when `onPress` is set. */
  accessibilityLabel?: TranslationKeys;
  /** Optional header slot — rendered above the body with a bottom divider. */
  header?: React.ReactNode;
  /** Optional footer slot — rendered below the body with a top divider. */
  footer?: React.ReactNode;
  /** Forwarded to the root element for testing. */
  testID?: string;
  style?: ViewStyle;
}

const VARIANT_CLASSES: Record<CardVariant, string> = {
  default: "bg-white border border-stroke-20 shadow-sm",
  elevated: "bg-white shadow-md",
  outlined: "bg-white border border-stroke-50",
};

export function Card({
  children,
  variant = "default",
  onPress,
  accessibilityLabel,
  header,
  footer,
  testID,
  style,
}: CardProps) {
  const { t } = useI18n();
  const containerClass = `rounded-xl overflow-hidden mb-3 ${VARIANT_CLASSES[variant]}`;

  const inner = (
    <>
      {header && (
        <>
          <View className="px-4 pt-4 pb-3">{header}</View>
          <View className="border-b border-stroke-20" />
        </>
      )}
      <View className="p-4">{children}</View>
      {footer && (
        <>
          <View className="border-t border-stroke-20" />
          <View className="px-4 pt-3 pb-4">{footer}</View>
        </>
      )}
    </>
  );

  if (onPress) {
    return (
      <TouchableOpacity
        testID={testID}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel ? t(accessibilityLabel) : undefined}
        onPress={onPress}
        className={`${containerClass} active:opacity-80`}
        style={style}
      >
        {inner}
      </TouchableOpacity>
    );
  }

  return (
    <View testID={testID} className={containerClass} style={style}>
      {inner}
    </View>
  );
}
